import { customElement, property } from "lit/decorators.js";
import { html, PropertyValues, TemplateResult } from "lit";
import { watchMixin } from "@/components/meta/watch_mixin";
import { createRef, Ref, ref } from "lit/directives/ref.js";
import "@/components/saved_annotations/saved_annotation_input";
import "@/components/markdown_editor";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { annotationState } from "@/state/Annotations";
import { userAnnotationState } from "@/state/UserAnnotations";
import { savedAnnotationState } from "@/state/SavedAnnotations";
import { courseState } from "@/state/Courses";
import { exerciseState } from "@/state/Exercises";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import { initTooltips, generateUniqueId } from "@/utilities";
import { DraftAnswer, draftAnswerState } from "@/state/DraftAnswer";

// Min and max of the annotation text is defined in the annotation model.
const maxLength = 10_000;

/**
 * This component represents a form for creating or editing saved annotations
 *
 * @element d-annotation-form
 *
 * @prop {string} annotationText - The text of the annotation
 * @prop {string} savedAnnotationId - The id of the saved annotation
 * @prop {boolean} disabled - Disables all buttons
 * @prop {boolean} hasErrors - Shows red validation styling
 * @prop {string} submitButtonText - The I18n key of the text for the submit button
 * @prop {number} draftId - The id of the draft answer
 * @prop {string} _annotationText - The current annotation text (Internal)
 * @prop {string} _savedAnnotationId - The current saved annotation id (Internal)
 * @prop {string} _savedAnnotationTitle - The current saved annotation title (Internal)
 * @prop {string} _savedAnnotationSearchInput - The current saved annotation search input (Internal)
 * @prop {boolean} saveAnnotation - Whether to save the annotation (Internal)
 *
 * @fires cancel - If a users uses the cancel button
 * @fires submit - If the users presses the submit button, detail contains {text: string, savedAnnotationId: string}
 */
@customElement("d-annotation-form")
export class AnnotationForm extends watchMixin(DodonaElement) {
    @property({ type: String, attribute: "annotation-text" })
    annotationText: string = "";

    @property({ type: String, attribute: "saved-annotation-id" })
    savedAnnotationId: string = "";

    @property({ type: Boolean })
    disabled = false;

    @property({ type: Boolean, attribute: "has-errors" })
    hasErrors = false;

    @property({ type: String, attribute: "submit-button-text" })
    submitButtonText: string = "";

    @property({ type: Number, attribute: "draft-id" })
    draftId: number | null;

    @property({ state: true })
    _annotationText = "";

    @property({ state: true })
    _savedAnnotationId = "";

    @property({ state: true })
    _savedAnnotationTitle: string | null = null;

    @property({ state: true })
    _savedAnnotationSearchInput = "";

    @property({ state: true })
    saveAnnotation = false;

    showDraft = true;

    private readonly helpIds = {
        help: `annotation-help-${generateUniqueId()}`,
        helpStudent: `annotation-help-student-${generateUniqueId()}`,
        count: `annotation-count-${generateUniqueId()}`,
        titleError: `saved-annotation-title-error-${generateUniqueId()}`,
    };

    private readonly textareaId = `annotation-text-${generateUniqueId()}`;

    get draftAnswer(): DraftAnswer | null {
        if (!this.draftId) return null;
        return draftAnswerState.get(this.draftId);
    }

    get savedAnnotationTitle(): string {
        return this._savedAnnotationTitle ?? this._annotationText.split(/\s+/).slice(0, 5).join(" ").slice(0, 40);
    }

    set savedAnnotationTitle(title: string) {
        this._savedAnnotationTitle = title;
    }

    inputRef: Ref<HTMLTextAreaElement> = createRef();
    titleRef: Ref<HTMLInputElement> = createRef();

    watch = {
        annotationText: () => {
            this._annotationText = this.annotationText;
        },
        savedAnnotationId: () => {
            this._savedAnnotationId = this.savedAnnotationId || "";
        },
        saveAnnotation: () => {
            this.listenForCloseIfEmpty();
        },
        _annotationText: () => {
            this.listenForCloseIfEmpty();
        },
        _savedAnnotationSearchInput: () => {
            this.listenForCloseIfEmpty();
        },
    };

    public focusTextArea(): void {
        this.inputRef.value.focus();
    }

    get type(): string {
        return annotationState.isQuestionMode ? "user_question" : "user_annotation";
    }

    get rows(): number {
        return Math.max(3, this._annotationText.split("\n").length + 1);
    }

    /**
     * Event callback for when the user clicks anywhere on the page.
     * If the click is not on the annotation form or the annotation button, the annotation form is closed.
     * @param e The click event
     */
    static closeForm(e: MouseEvent): void {
        if (!(e.target instanceof Element)) return;

        if (!e.target.closest("d-annotation-form") && !e.target.closest(".annotation-button")) {
            userAnnotationState.formShown = false;
            userAnnotationState.selectedRange = null;
        }
    }

    isListeningForClose = false;

    listenForClose(): void {
        if (!this.isListeningForClose) {
            document.addEventListener("click", AnnotationForm.closeForm);
            this.isListeningForClose = true;
        }
    }

    stopListeningForClose(): void {
        if (this.isListeningForClose) {
            document.removeEventListener("click", AnnotationForm.closeForm);
            this.isListeningForClose = false;
        }
    }

    get isEmpty(): boolean {
        return (
            this._annotationText.length === 0 && !this.saveAnnotation && this._savedAnnotationSearchInput.length === 0
        );
    }

    listenForCloseIfEmpty(): void {
        if (this.isEmpty) {
            this.listenForClose();
        } else {
            this.stopListeningForClose();
        }
    }

    connectedCallback(): void {
        super.connectedCallback();
        this.listenForCloseIfEmpty();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.stopListeningForClose();
    }

    onSavedAnnotationInput(e: CustomEvent): void {
        if (e.detail.text) {
            this._annotationText = e.detail.text;
        }
        this._savedAnnotationId = e.detail.id;
        this._savedAnnotationSearchInput = e.detail.title;
    }

    onTextInput(): void {
        const input = this.inputRef.value;
        this._annotationText = input.value;
    }

    private onCancelClick(): void {
        this.handleCancel();
    }

    handleCancel(): void {
        const event = new CustomEvent("cancel", { bubbles: true, composed: true });
        this.dispatchEvent(event);
    }

    private onNativeSubmit(e: Event): void {
        e.preventDefault();
        e.stopPropagation();
        this.handleSubmit();
    }

    handleSubmit(): void {
        if (this.disabled) return;

        this.hasErrors = false;
        this.disabled = true;

        let dispatched = false;
        try {
            const input = this.inputRef.value;
            if (!input || !input.reportValidity()) {
                this.hasErrors = true;
                return; // Something is wrong, abort.
            }

            if (this.saveAnnotation) {
                if (this.isTitleTaken && !confirm(I18n.t("js.saved_annotation.confirm_title_taken"))) {
                    return; // User cancelled.
                }
                const title = this.titleRef.value;
                if (!title?.reportValidity()) {
                    return;
                }
            }

            const event = new CustomEvent("submit", {
                detail: {
                    text: this._annotationText,
                    savedAnnotationId: this._savedAnnotationId,
                    savedAnnotationTitle: this.savedAnnotationTitle,
                    saveAnnotation: this.saveAnnotation,
                },
                bubbles: true,
                composed: true,
            });
            this.dispatchEvent(event);
            dispatched = true;
        } finally {
            // Keep old UX: after a successful submit event, the parent usually replaces/closes the form.
            // But if we bailed early (validation/confirm/error), re-enable controls.
            if (!dispatched) {
                this.disabled = false;
            }
        }
    }

    onKeyDown(e: KeyboardEvent): void {
        if (e.code === "Enter" && (e.shiftKey || e.ctrlKey)) {
            // Send using Shift-Enter or Ctrl-Enter.
            e.preventDefault();
            this.handleSubmit();
        } else if (e.code === "Escape") {
            // Cancel using ESC.
            e.preventDefault();
            this.handleCancel();
        }
    }

    onTitleInput(): void {
        const input = this.titleRef.value;
        this.savedAnnotationTitle = input.value;
    }

    firstUpdated(): void {
        this.inputRef.value.focus();
    }

    updated(changedProperties: PropertyValues): void {
        // Prefill draft once (no side effects in render)
        if (!annotationState.isQuestionMode && this.draftAnswer && this.showDraft && !this.annotationText) {
            this.showDraft = false;
            this._annotationText = this.draftAnswer.draft_answer;
        }

        // Focus the newly shown title input if the user wants to save the annotation.
        if (changedProperties.has("saveAnnotation") && this.saveAnnotation) {
            this.titleRef.value?.focus();
            this.titleRef.value?.select();
        }

        initTooltips(this);
    }

    toggleSaveAnnotation(): void {
        this.saveAnnotation = !this.saveAnnotation;
    }

    private readonly onToggleSaveAnnotation = (): void => {
        this.toggleSaveAnnotation();
    };

    get canSaveAnnotation(): boolean {
        return !annotationState.isQuestionMode;
    }

    get potentialSavedAnnotationsExist(): boolean {
        return (
            (
                savedAnnotationState.getList(
                    new Map([
                        ["course_id", courseState.id.toString()],
                        ["exercise_id", exerciseState.id.toString()],
                    ]),
                ) || []
            ).length > 0
        );
    }

    get isTitleTaken(): boolean {
        return savedAnnotationState.isTitleTaken(this.savedAnnotationTitle, exerciseState.id, courseState.id);
    }

    private get showStudentHelp(): boolean {
        return annotationState.isQuestionMode;
    }

    private get annotationTextDescribedBy(): string {
        const ids = [this.helpIds.help, this.helpIds.count];
        if (this.showStudentHelp) ids.push(this.helpIds.helpStudent);
        return ids.join(" ");
    }

    private get hasSavedAnnotationColumn(): boolean {
        return this.canSaveAnnotation && this.potentialSavedAnnotationsExist;
    }

    private get mainColumnClasses(): string {
        return this.hasSavedAnnotationColumn ? "col-12 col-lg-8" : "col-12";
    }

    private get sideColumnClasses(): string {
        return this.hasSavedAnnotationColumn ? "col-12 col-lg-4" : "col-12";
    }

    private get buttonColumnClasses(): string {
        const spacing = this.hasSavedAnnotationColumn ? "mt-2 mt-lg-0" : "mt-2";
        return `${this.sideColumnClasses} ${spacing} text-end`;
    }

    render(): TemplateResult {
        // i18n-tasks-use t('js.user_question.send') t('js.user_question.update') t('js.user_question.reply') t('js.user_annotation.send') t('js.user_annotation.update') t('js.user_question.fields.annotation_text') t('js.user_annotation.fields.annotation_text')
        return html`
            <form class="annotation-submission form" @submit=${this.onNativeSubmit}>
                ${
                    !annotationState.isQuestionMode && this.draftAnswer
                        ? html`
                              <div class="draft-title mb-2 mt-1">
                                  <i
                                      class="mdi mdi-creation"
                                      aria-hidden="true"
                                      title="${I18n.t("js.annotation.draft.description")}"
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="top"
                                  ></i>
                                  <span class="visually-hidden">${I18n.t("js.annotation.draft.description")}</span>
                                  ${I18n.t("js.annotation.draft.title")}
                              </div>
                          `
                        : ""
                }

                <div class="row">
                    <div class="${this.mainColumnClasses}">
                        <div class="field form-group">
                            <label class="visually-hidden" for="${this.textareaId}">
                                ${I18n.t(`js.${this.type}.fields.annotation_text`)}
                            </label>
                            <d-markdown-editor>
                                <textarea
                                    id="${this.textareaId}"
                                    autofocus
                                    required
                                    class="form-control annotation-submission-input ${
                                        this.hasErrors ? "validation-error" : ""
                                    }"
                                    aria-invalid="${this.hasErrors ? "true" : "false"}"
                                    aria-describedby="${this.annotationTextDescribedBy}"
                                    data-testid="annotation-textarea"
                                    .rows=${this.rows}
                                    minlength="1"
                                    maxlength="${maxLength}"
                                    .value=${this._annotationText}
                                    ${ref(this.inputRef)}
                                    @keydown=${this.onKeyDown}
                                    @input=${this.onTextInput}
                                ></textarea>
                            </d-markdown-editor>

                            <div class="clearfix annotation-help-block">
                                <span id="${this.helpIds.help}" class="help-block">
                                    ${unsafeHTML(I18n.t("js.user_annotation.help"))}
                                </span>

                                ${
                                    this.showStudentHelp
                                        ? html`
                                              <span id="${this.helpIds.helpStudent}" class="help-block">
                                                  ${unsafeHTML(I18n.t("js.user_annotation.help_student"))}
                                              </span>
                                          `
                                        : ""
                                }

                                <span id="${this.helpIds.count}" class="help-block float-end" aria-live="polite">
                                    <span class="used-characters"
                                        >${I18n.formatNumber(this._annotationText.length)}</span
                                    >
                                    / ${I18n.formatNumber(maxLength)}
                                </span>
                            </div>
                        </div>
                    </div>

                    ${
                        this.hasSavedAnnotationColumn
                            ? html`
                                  <div class="${this.sideColumnClasses}">
                                      <d-saved-annotation-input
                                          name="saved_annotation_id"
                                          class="saved-annotation-input"
                                          .value=${this._savedAnnotationId}
                                          annotation-text="${this._annotationText}"
                                          @input=${this.onSavedAnnotationInput}
                                          .disabled=${this.saveAnnotation}
                                      ></d-saved-annotation-input>
                                  </div>
                              `
                            : ""
                    }
                </div>

                <div class="row mb-1">
                    <div class="${this.mainColumnClasses} align-items-center d-flex">
                        ${
                            this.canSaveAnnotation && this._savedAnnotationId == ""
                                ? html`
                                      <div class="field form-group mb-0 d-flex align-items-center flex-grow-1">
                                          <div class="form-check save-annotation-check">
                                              <input
                                                  class="form-check-input"
                                                  type="checkbox"
                                                  @change=${this.onToggleSaveAnnotation}
                                                  data-testid="check-save-annotation"
                                                  id="check-save-annotation"
                                                  .checked=${this.saveAnnotation}
                                              />
                                              <label class="form-check-label" for="check-save-annotation">
                                                  ${I18n.t("js.user_annotation.fields.saved_annotation_title")}
                                              </label>
                                          </div>

                                          ${
                                              this.saveAnnotation
                                                  ? html`
                                                        <div class="saved-annotation-title flex-grow-1">
                                                            <input
                                                                required
                                                                class="form-control ${this.isTitleTaken ? "is-invalid" : ""}"
                                                                aria-invalid="${this.isTitleTaken ? "true" : "false"}"
                                                                aria-describedby="${
                                                                    this.isTitleTaken ? this.helpIds.titleError : ""
                                                                }"
                                                                data-testid="annotation-title-input"
                                                                type="text"
                                                                ${ref(this.titleRef)}
                                                                @keydown=${this.onKeyDown}
                                                                @input=${this.onTitleInput}
                                                                .value=${this.savedAnnotationTitle}
                                                                id="saved-annotation-title"
                                                                title="${
                                                                    this.isTitleTaken
                                                                        ? I18n.t("js.saved_annotation.title_taken")
                                                                        : ""
                                                                }"
                                                            />
                                                            <label for="saved-annotation-title">
                                                                ${I18n.t("js.saved_annotation.title")}:
                                                            </label>
                                                            ${
                                                                this.isTitleTaken
                                                                    ? html`<span
                                                                          id="${this.helpIds.titleError}"
                                                                          class="visually-hidden"
                                                                          >${I18n.t("js.saved_annotation.title_taken")}</span
                                                                      >`
                                                                    : ""
                                                            }
                                                        </div>
                                                    `
                                                  : ""
                                          }
                                      </div>
                                  `
                                : ""
                        }
                    </div>

                    <div class="${this.buttonColumnClasses}">
                        <button
                            class="btn btn-text"
                            type="button"
                            @click=${this.onCancelClick}
                            .disabled=${this.disabled}
                        >
                            ${I18n.t("js.user_annotation.cancel")}
                        </button>
                        <button class="btn btn-filled" type="submit" .disabled=${this.disabled}>
                            ${I18n.t(`js.${this.type}.${this.submitButtonText}`)}
                        </button>
                    </div>
                </div>
            </form>
        `;
    }
}
